import { useCallback, useEffect, useState } from "react";
import * as HoverCard from "@radix-ui/react-hover-card";
import * as Slider from "@radix-ui/react-slider";
import { Flex, Text, Button } from "@radix-ui/themes";
import { FaChevronRight } from "react-icons/fa";

import { marketplaceTokens } from "@/lib/tokens";
import { cn } from "@/lib/utils";
import FilterFooter from "./shared/FilterFooter";
import useFilterDropdown from "./shared/useFilterDropdown";
import { MarketplaceFilterKey, MarketplaceUpdates } from "../types";

type BPMFilterProps = {
  parameter?: string;
  onSetFilter: (updates: MarketplaceUpdates) => void;
  onRemoveFilter: (param: MarketplaceFilterKey) => void;
};

export default function BPMFilter({
  parameter,
  onSetFilter,
  onRemoveFilter,
}: BPMFilterProps) {
  const [sliderValue, setSliderValue] = useState<number[]>([20, 240]);
  const [minInput, setMinInput] = useState<string>("20");
  const [maxInput, setMaxInput] = useState<string>("240");

  const restoreFromParameter = useCallback(() => {
    if (parameter && parameter.includes("-")) {
      const [start, end] = parameter.split("-");
      setSliderValue([parseInt(start, 10), parseInt(end, 10)]);
    } else {
      setSliderValue([20, 240]);
    }
  }, [parameter]);

  const {
    open,
    isPinned,
    handleOpenChange,
    handleTriggerClick,
    handleInteractOutside,
    close,
  } = useFilterDropdown({ id: "bpm", onRestore: restoreFromParameter });

  // Sync with URL parameters
  useEffect(() => {
    restoreFromParameter();
  }, [parameter, restoreFromParameter]);

  // Sync input text when slider changes
  useEffect(() => {
    setMinInput(sliderValue[0].toString());
    setMaxInput(sliderValue[1].toString());
  }, [sliderValue]);

  function handleApply() {
    onSetFilter({ bpm: `${sliderValue[0]}-${sliderValue[1]}` });
    close({ restore: false });
  }

  function handleReset() {
    setSliderValue([20, 240]);
    onRemoveFilter("bpm");
    close({ restore: false });
  }

  const hasSelection = Boolean(parameter);

  return (
    <HoverCard.Root
      open={open}
      onOpenChange={handleOpenChange}
      openDelay={isPinned ? 0 : 200}
      closeDelay={isPinned ? 0 : 100}
    >
      <HoverCard.Trigger asChild>
        <Button
          onClick={handleTriggerClick}
          className={cn(
            "flex items-center gap-[8px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 focus-visible:ring-offset-black",
            marketplaceTokens.filterItem,
            hasSelection && "!text-white !bg-background-neutral-inverted",
            {
              [marketplaceTokens.filterItemActive]: open,
              [marketplaceTokens.filterItemInactive]: !open,
            },
          )}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-pressed={isPinned}
        >
          <Text className="flex-1">BPM</Text>
          <FaChevronRight
            size={10}
            aria-hidden
            className={cn("transition-transform", {
              "rotate-90": open,
            })}
          />
        </Button>
      </HoverCard.Trigger>

      <HoverCard.Portal>
        <HoverCard.Content
          side="bottom"
          avoidCollisions={false}
          align="start"
          sideOffset={2}
          className="w-[350px] bg-background-neutral-inverted rounded shadow-[0px_10px_18px_-1px_rgba(5,12,43,0.14)] pt-4 px-4 pb-3 flex flex-col gap-4 z-50"
          onInteractOutside={handleInteractOutside}
        >
          {/* Slider */}
          <Flex direction="column" className="gap-4">
            <Slider.Root
              className="relative flex items-center select-none touch-none w-full h-5"
              value={sliderValue}
              onValueChange={setSliderValue}
              min={20}
              max={240}
              step={1}
              minStepsBetweenThumbs={1}
            >
              <Slider.Track className="bg-gray-700 relative grow rounded-full h-[5px]">
                <Slider.Range className="absolute bg-teal-500 rounded-full h-full shadow-[0_0_8px_rgba(48,66,226,0.3)]" />
              </Slider.Track>
              <Slider.Thumb className="block w-5 h-5 bg-white border-2 border-gray-200 rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.15)] hover:scale-110 hover:shadow-[0_3px_12px_rgba(0,0,0,0.25)] focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-offset-black cursor-pointer transition-all duration-200 ease-out active:scale-105" />
              <Slider.Thumb className="block w-5 h-5 bg-white border-2 border-gray-200 rounded-full shadow-[0_2px_8px_rgba(0,0,0,0.15)] hover:scale-110 hover:shadow-[0_3px_12px_rgba(0,0,0,0.25)] focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-offset-black cursor-pointer transition-all duration-200 ease-out active:scale-105" />
            </Slider.Root>

            {/* Input Fields */}
            <Flex className="gap-3 items-center">
              <div className="relative flex-1">
                <input
                  type="text"
                  inputMode="numeric"
                  value={minInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\d]/g, "");
                    setMinInput(val);

                    // Update slider if valid
                    if (val !== "") {
                      const num = parseInt(val);
                      if (num >= 20 && num <= 240) {
                        setSliderValue([num, sliderValue[1]]);
                      }
                    }
                  }}
                  onBlur={() => {
                    if (minInput === "" || parseInt(minInput) < 20) {
                      setMinInput("20");
                      setSliderValue([20, sliderValue[1]]);
                    } else if (parseInt(minInput) > 240) {
                      setMinInput("240");
                      setSliderValue([240, sliderValue[1]]);
                    }
                  }}
                  className="w-full h-[38px] pl-3 pr-12 bg-white border border-gray-400 rounded-md text-[15px] font-semibold text-foreground-primary focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] font-normal text-gray-500 pointer-events-none">
                  BPM
                </span>
              </div>

              <span className="text-gray-600 text-[13px]">—</span>

              <div className="relative flex-1">
                <input
                  type="text"
                  inputMode="numeric"
                  value={maxInput}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^\d]/g, "");
                    setMaxInput(val);

                    // Update slider if valid
                    if (val !== "") {
                      const num = parseInt(val);
                      if (num >= 20 && num <= 240) {
                        setSliderValue([sliderValue[0], num]);
                      }
                    }
                  }}
                  onBlur={() => {
                    if (maxInput === "" || parseInt(maxInput) < 20) {
                      setMaxInput("20");
                      setSliderValue([sliderValue[0], 20]);
                    } else if (parseInt(maxInput) > 240) {
                      setMaxInput("240");
                      setSliderValue([sliderValue[0], 240]);
                    }
                  }}
                  className="w-full h-[38px] pl-3 pr-12 bg-white border border-gray-400 rounded-md text-[15px] font-semibold text-foreground-primary focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] font-normal text-gray-500 pointer-events-none">
                  BPM
                </span>
              </div>
            </Flex>
          </Flex>

          {/* Footer */}
          <FilterFooter onReset={handleReset} onApply={handleApply} />
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
}
